import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoginForm, type LoginFormValues } from "@opero/ui/auth";
import {
  AuthSeparator,
  MfaForm,
  PasskeyButton,
  PasskeyPrompt,
  SsoButtons,
} from "@opero/ui/auth";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Spinner,
  PasswordInput,
  BrandLockup,
} from "@opero/ui";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import {
  assertPasskey,
  buildTokenRedirectUrl,
  cancelPasskeyCeremony,
  isMfaRequired,
  isPasskeyCancellation,
  isWorkspaceSetupRequired,
  PasskeyError,
  supportsPasskeyAutofill,
  supportsPasskeys,
  type AuthLoginResult,
  type LoginResponse,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@opero/shared";
import { polaris, APP_URL } from "../lib/polaris-client";
import {
  discoverLoginMethod,
  redirectToIdp,
  startSsoByEmail,
} from "../lib/sso";
import { useSsoProviders } from "../hooks/use-sso-providers";
import { AuthModalShell } from "./AuthModalShell";

/**
 * What SSO discovery said about the address currently in the email box.
 *
 * `null` until an address has been typed — enforcement is decided per verified
 * domain, so before then the screen can only show the tenant-wide buttons.
 */
type Discovery = {
  email: string;
  mode: "sso_required" | "sso_optional" | "password";
  providerLabel?: string;
  initiateUrl?: string;
};

type ModalView =
  | "login"
  | "mfa"
  | "forgot-password"
  | "forgot-success"
  | "reset-password"
  | "reset-success";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignup?: () => void;
  initialView?: ModalView;
}

export function LoginModal({
  isOpen,
  onClose,
  onSwitchToSignup,
  initialView = "login",
}: LoginModalProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  /** Which factors the server will accept for `mfaToken` — drives the switch
   *  between the code boxes and the passkey prompt. */
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);
  const [mfaMode, setMfaMode] = useState<"totp" | "passkey">("totp");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  /** True once a conditional-mediation ceremony is listening on the email box. */
  const [passkeyAutofill, setPasskeyAutofill] = useState(false);
  const [view, setView] = useState<ModalView>(initialView);
  const [forgotEmail, setForgotEmail] = useState("");
  const [discovery, setDiscovery] = useState<Discovery | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const resetToken = searchParams.get("token") || "";
  const justVerified = searchParams.get("verified") === "1";
  const justProvisioned = searchParams.get("provisioned") === "1";
  const setupReason = searchParams.get("reason");

  const redirectTo = searchParams.get("redirect") || APP_URL;

  useEffect(() => {
    if (isOpen) {
      setView(initialView);
      setError(null);
      setMfaToken(null);
      setMfaMethods([]);
      setMfaMode("totp");
      setDiscovery(null);
      setForgotEmail("");
      setResetPassword("");
      setResetConfirm("");
    }
  }, [isOpen, initialView]);

  const {
    providers: ssoProviders,
    redirecting: ssoRedirecting,
    select: handleSsoSelect,
  } = useSsoProviders({
    isOpen,
    mode: "login",
    onError: useCallback((message: string | null) => setError(message), []),
  });

  const redirectToApp = (response: LoginResponse) => {
    window.location.href = buildTokenRedirectUrl(
      redirectTo,
      response.accessToken ?? "",
      response.refreshToken ?? "",
    );
  };

  const passkeysAvailable = useMemo(() => supportsPasskeys(), []);

  /**
   * The three things a completed sign-in can be, in one place.
   *
   * Password, passkey and SSO all land on the same fork — a second factor is
   * still owed, the workspace has never been provisioned, or there is a
   * session — so the branch belongs to the flow, not to any one entry point.
   */
  const completeLogin = useCallback(
    (result: AuthLoginResult) => {
      if (isMfaRequired(result)) {
        setMfaToken(result.mfaToken);
        setMfaMethods(result.methods ?? []);
        /* Offer the passkey first when the account has one: it is one gesture
           against six typed digits, and the code boxes stay a click away. */
        setMfaMode(
          (result.methods ?? []).includes("passkey") && supportsPasskeys()
            ? "passkey"
            : "totp",
        );
        setView("mfa");
      } else if (isWorkspaceSetupRequired(result)) {
        navigate("/setup", {
          state: {
            setupToken: result.setupToken,
            expiresIn: result.expiresIn,
          },
        });
      } else {
        window.location.href = buildTokenRedirectUrl(
          redirectTo,
          result.accessToken ?? "",
          result.refreshToken ?? "",
        );
      }
    },
    [navigate, redirectTo],
  );

  /**
   * Resolves how this address is allowed to sign in, as soon as it is typed.
   *
   * Best-effort: discovery is an optimisation over "show the password box and
   * let the server say no", so a failure leaves the form exactly as it was
   * rather than blocking a login that would have worked.
   */
  const resolveDiscovery = useCallback(
    async (email: string): Promise<Discovery | null> => {
      try {
        const result = await discoverLoginMethod(email);
        const next: Discovery = {
          email,
          mode: result.mode,
          providerLabel: result.providerLabel,
          initiateUrl: result.initiateUrl,
        };
        setDiscovery(next);
        return next;
      } catch {
        setDiscovery(null);
        return null;
      }
    },
    [],
  );

  const handleEmailBlur = useCallback(
    (email: string) => {
      void resolveDiscovery(email);
    },
    [resolveDiscovery],
  );

  /** Hands off to the IdP discovery named for the address in the email box. */
  const continueWithSso = async (email: string, initiateUrl?: string) => {
    setError(null);
    setIsLoading(true);
    try {
      if (initiateUrl) redirectToIdp(initiateUrl);
      else await startSsoByEmail(email);
    } catch (err: unknown) {
      setIsLoading(false);
      setError(getErrorMessage(err, "Couldn't start SSO sign-in"));
    }
  };

  const handleLogin = async (values: LoginFormValues) => {
    /* Pressing Enter in the email box submits without blurring it, so the
       discovery on hand can be for the *previous* address. Re-resolve that one
       case rather than trusting it — otherwise a second address inherits the
       first one's verdict, hidden password box and all. */
    const resolved =
      discovery && discovery.email !== values.email
        ? await resolveDiscovery(values.email)
        : discovery;

    // The domain refuses password login, so submitting the credential would
    // only earn a rejection — Enter on this form is the SSO handoff instead.
    if (resolved?.mode === "sso_required") {
      await continueWithSso(values.email, resolved.initiateUrl);
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const result = (await polaris.auth.login({
        // Generated login request-body type is mis-referenced upstream; cast
        // the credentials to the SDK's declared body type.
        body: {
          email: values.email,
          password: values.password,
        } as unknown as Parameters<typeof polaris.auth.login>[0]["body"],
      })) as AuthLoginResult;
      completeLogin(result);
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Invalid email or password"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * First-factor passkey sign-in — no address, no password.
   *
   * The options carry no `allowCredentials`, so the browser offers every
   * passkey registered for this domain and the *user* is discovered from the
   * one they pick. `autofill` runs the same ceremony under conditional
   * mediation, where the browser surfaces those passkeys inside its own
   * dropdown on the email box instead of a modal sheet.
   */
  const runPasskeyLogin = useCallback(
    async ({ autofill = false }: { autofill?: boolean } = {}) => {
      if (!autofill) {
        setError(null);
        setPasskeyLoading(true);
        /* An explicit ceremony aborts the conditional one, so the email box is
           about to stop having anything behind its `webauthn` hint. */
        setPasskeyAutofill(false);
      }
      try {
        const { challengeId, options } =
          await polaris.auth.passkeyLoginOptions();
        const assertion = await assertPasskey(
          options as unknown as PublicKeyCredentialRequestOptionsJSON,
          { autofill },
        );
        const result = (await polaris.auth.passkeyLogin({
          body: {
            challengeId,
            response: assertion as unknown as Record<string, unknown>,
          },
        })) as AuthLoginResult;
        completeLogin(result);
      } catch (err: unknown) {
        /* A conditional ceremony is speculative — it is aborted by every
           competing prompt and by leaving the view — so it never speaks. */
        if (autofill || isPasskeyCancellation(err)) return;
        setError(
          err instanceof PasskeyError
            ? err.message
            : getErrorMessage(err, "Couldn't sign in with that passkey"),
        );
      } finally {
        if (autofill) setPasskeyAutofill(false);
        else setPasskeyLoading(false);
      }
    },
    [completeLogin],
  );

  /**
   * Arms the browser's own passkey dropdown on the email box.
   *
   * Kept to the login view: the ceremony must be cancelled before any explicit
   * one can start, and only one can be in flight per page.
   */
  useEffect(() => {
    if (!isOpen || view !== "login" || !passkeysAvailable) return;
    let cancelled = false;
    void (async () => {
      if (!(await supportsPasskeyAutofill()) || cancelled) return;
      // The input must already carry `autocomplete="… webauthn"` when the
      // ceremony starts, so paint the attribute before asking for options.
      setPasskeyAutofill(true);
      await runPasskeyLogin({ autofill: true });
    })();
    return () => {
      cancelled = true;
      setPasskeyAutofill(false);
      cancelPasskeyCeremony();
    };
  }, [isOpen, view, passkeysAvailable, runPasskeyLogin]);

  /** Second-factor passkey — scoped to the user the `mfaToken` names. */
  const handlePasskeyMfa = async () => {
    if (!mfaToken) return;
    setError(null);
    setPasskeyLoading(true);
    try {
      const options = await polaris.auth.authenticateOptions({
        body: { mfaToken },
      });
      const assertion = await assertPasskey(
        options as unknown as PublicKeyCredentialRequestOptionsJSON,
      );
      redirectToApp(
        await polaris.auth.authenticate({
          body: {
            mfaToken,
            response: assertion as unknown as Record<string, unknown>,
          },
        }),
      );
    } catch (err: unknown) {
      if (isPasskeyCancellation(err)) return;
      setError(
        err instanceof PasskeyError
          ? err.message
          : getErrorMessage(err, "Couldn't verify your passkey"),
      );
    } finally {
      setPasskeyLoading(false);
    }
  };

  /** Back to the credential form, with the half-finished challenge dropped. */
  const cancelMfa = () => {
    setMfaToken(null);
    setMfaMethods([]);
    setMfaMode("totp");
    setError(null);
    setView("login");
  };

  const handleMfa = async (code: string, trustDevice: boolean) => {
    if (!mfaToken) return;
    setError(null);
    setIsLoading(true);
    try {
      redirectToApp(
        await polaris.auth.verifyMfaChallenge({
          body: (trustDevice
            ? { mfaToken, code, trustDevice: true, trustDuration: "30days" }
            : { mfaToken, code }) as unknown as Parameters<
            typeof polaris.auth.verifyMfaChallenge
          >[0]["body"],
        }),
      );
    } catch {
      setError("Invalid verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await polaris.auth.forgotPassword({
        body: {
          email: forgotEmail,
          redirectUrl: window.location.origin,
        } as unknown as Parameters<
          typeof polaris.auth.forgotPassword
        >[0]["body"],
      });
    } catch {
      /* don't reveal */
    }
    setView("forgot-success");
    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetPassword !== resetConfirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await polaris.auth.resetPassword({
        body: {
          token: resetToken,
          newPassword: resetPassword,
          confirmPassword: resetConfirm,
        } as unknown as Parameters<
          typeof polaris.auth.resetPassword
        >[0]["body"],
      });
      setView("reset-success");
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to reset password"));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Discovery only speaks for the address that produced it — the buttons below
   * are the tenant-wide list and stay put, but the notice must disappear the
   * moment the email box holds something else.
   */
  const ssoRequired = discovery?.mode === "sso_required";
  const showSsoNotice = discovery !== null && discovery.mode !== "password";
  /* An SSO-enforced domain has no local credential to assert, passkey
     included — the handoff button is the only route on offer. */
  const showPasskeyButton = passkeysAvailable && !ssoRequired;
  const offersPasskey = passkeysAvailable && mfaMethods.includes("passkey");
  const offersTotp = mfaMethods.includes("totp");
  return (
    <AuthModalShell isOpen={isOpen} onClose={onClose} brand={<BrandLockup />}>
      <div className="px-7 pb-7 pt-2">
        {view === "login" && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Sign in to Opero
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Enter your credentials to access your workspace
              </p>
            </div>
            {justVerified && (
              <div className="mb-4 rounded-lg border border-accent-500/20 bg-accent-500/5 p-3 text-sm text-slate-700 dark:text-slate-200">
                Your email is verified. Sign in to finish setting up your
                workspace.
              </div>
            )}
            {justProvisioned && (
              <div className="mb-4 rounded-lg border border-accent-500/20 bg-accent-500/5 p-3 text-sm text-slate-700 dark:text-slate-200">
                Your workspace is ready! Sign in to continue.
              </div>
            )}
            {setupReason === "setup-token-missing" && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                No workspace setup in progress. Sign in to continue.
              </div>
            )}
            {setupReason === "setup-expired" && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                Your workspace setup link expired. Sign in to get a new one.
              </div>
            )}
            {(ssoProviders.length > 0 || showPasskeyButton) && (
              /* The routes that skip the password, above the box that asks for
                 one. `SsoButtons` draws the closing rule when it is present;
                 the passkey button alone still needs one. */
              <div className="mb-5 space-y-4">
                {showPasskeyButton && (
                  <PasskeyButton
                    onClick={() => runPasskeyLogin()}
                    isLoading={passkeyLoading}
                    disabled={isLoading || ssoRedirecting}
                  />
                )}
                {ssoProviders.length > 0 ? (
                  <SsoButtons
                    providers={ssoProviders}
                    onSelect={handleSsoSelect}
                    isLoading={ssoRedirecting || isLoading}
                    separatorLabel="Or sign in with email"
                  />
                ) : (
                  <AuthSeparator label="Or sign in with email" />
                )}
              </div>
            )}
            <LoginForm
              onSubmit={handleLogin}
              isLoading={isLoading}
              error={error}
              onEmailBlur={handleEmailBlur}
              /* An SSO-enforced domain has no password to offer — the box is
                 replaced by the handoff button in `notice`. */
              passwordHidden={ssoRequired}
              passkeyAutofill={passkeyAutofill}
              notice={
                showSsoNotice ? (
                  <div className="space-y-3 rounded-lg border border-accent-500/20 bg-accent-500/5 p-3">
                    <p className="text-sm text-slate-700 dark:text-slate-200">
                      {ssoRequired
                        ? `${discovery!.email} signs in through your organization's identity provider.`
                        : `${discovery!.email} can sign in through your organization's identity provider.`}
                    </p>
                    <Button
                      type="button"
                      className="w-full"
                      variant={ssoRequired ? "default" : "outline"}
                      disabled={isLoading || ssoRedirecting}
                      onClick={() =>
                        continueWithSso(
                          discovery!.email,
                          discovery!.initiateUrl,
                        )
                      }
                    >
                      {isLoading ? <Spinner size="sm" /> : null}
                      Continue with {discovery!.providerLabel ?? "SSO"}
                    </Button>
                  </div>
                ) : null
              }
              onForgotPassword={() => {
                setError(null);
                setView("forgot-password");
              }}
              onSignup={onSwitchToSignup}
            />
          </>
        )}
        {view === "mfa" &&
          (mfaMode === "passkey" ? (
            <PasskeyPrompt
              onConfirm={handlePasskeyMfa}
              onCancel={cancelMfa}
              isLoading={passkeyLoading}
              error={error}
              alternate={
                offersTotp ? (
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setMfaMode("totp");
                    }}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Use an authenticator code instead
                  </button>
                ) : null
              }
            />
          ) : (
            <>
              <MfaForm
                onSubmit={handleMfa}
                onCancel={cancelMfa}
                isLoading={isLoading}
                error={error}
              />
              {offersPasskey && (
                <p className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setMfaMode("passkey");
                    }}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Use a passkey instead
                  </button>
                </p>
              )}
            </>
          ))}
        {view === "forgot-password" && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Reset your password
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                We&apos;ll send you a link to reset your password
              </p>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner size="sm" /> Sending...
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
              <p className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setView("login");
                  }}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Back to login
                </button>
              </p>
            </form>
          </>
        )}
        {view === "forgot-success" && (
          <div className="space-y-4 text-center py-4">
            <div className="rounded-lg border border-accent-500/20 bg-accent-500/5 p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Check your email
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                If an account exists for <strong>{forgotEmail}</strong>,
                we&apos;ve sent a password reset link.
              </p>
            </div>
            <button
              onClick={() => setView("login")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Back to login
            </button>
          </div>
        )}

        {view === "reset-password" && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Set New Password
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Enter your new password below
              </p>
            </div>
            {!resetToken ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-red-500">
                  Invalid or missing reset link.
                </p>
                <button
                  onClick={() => {
                    setError(null);
                    setView("forgot-password");
                  }}
                  className="text-sm font-medium text-primary-600"
                >
                  Request a new link
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-5">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription className="text-sm text-danger">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="space-y-1.5">
                  <Label>New password</Label>
                  <PasswordInput
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Confirm password</Label>
                  <PasswordInput
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Spinner size="sm" /> Resetting...
                    </>
                  ) : (
                    "Reset password"
                  )}
                </Button>
              </form>
            )}
          </>
        )}

        {view === "reset-success" && (
          <div className="space-y-4 text-center py-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-accent-500/10 flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-accent-500"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Password Updated
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your password has been reset successfully.
            </p>
            <button
              onClick={() => setView("login")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Sign in with new password
            </button>
          </div>
        )}
      </div>
    </AuthModalShell>
  );
}
